import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBSTACK_SUBSCRIBE_URL = "https://clementcourse.substack.com/subscribe";

const COLLECTIONS = [
  "Scenic Petite",
  "Scenic",
  "Course I",
  "Founder Series",
  "Bespoke Commission",
];

export default function Waitlist() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    preferred_collection: "",
    country: "",
    home_club: "",
    personal_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState("");

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !form.email.trim() ||
      !form.preferred_collection
    ) {
      setError(
        "Please complete first name, last name, email and preferred collection.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        preferred_collection: form.preferred_collection,
        country: form.country.trim() || null,
        home_club: form.home_club.trim() || null,
        personal_notes: form.personal_notes.trim() || null,
      };
      await axios.post(`${API}/waitlist`, payload);
      setSubmittedEmail(payload.email);
      setSuccess(true);
      toast.success("Your registry submission has been received.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid={TID.sectionWaitlist}
      className="relative py-32 sm:py-48 border-t border-[rgba(197,160,89,0.15)] bg-[#0C0F0A] overflow-hidden"
    >
      {/* Editorial dark backdrop — quiet luxury, no people */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_60%_at_85%_15%,rgba(197,160,89,0.10),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_45%_at_8%_95%,rgba(48,72,55,0.40),transparent_65%)]" />
        <div className="absolute inset-y-0 left-[10%] w-px bg-gradient-to-b from-transparent via-[rgba(197,160,89,0.15)] to-transparent" />
        <div className="absolute inset-y-0 right-[14%] w-px bg-gradient-to-b from-transparent via-[rgba(197,160,89,0.08)] to-transparent" />
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix type='matrix' values='0 0 0 0 0.95 0 0 0 0 0.85 0 0 0 0 0.55 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
        {/* Header — editorial, centered, restrained */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-4">
            <span className="block w-12 h-px bg-[#C5A059]" />
            <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
              The Private Registry
            </span>
            <span className="block w-12 h-px bg-[#C5A059]" />
          </div>

          <h2 className="mt-10 font-serif-display text-[#F5F2EB] font-light text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
            Request Access
          </h2>

          <div className="mt-12 space-y-6 max-w-xl mx-auto text-[#C9C6BC] text-[15px] sm:text-base leading-[1.85] font-light">
            <p>
              CLEMENT pieces are produced in limited quantities and released
              only when they meet our standards.
            </p>
            <p>
              Membership in the registry secures priority allocation, advance
              access to new collections, admission to bespoke commissioning,
              and member-only perks &mdash; releases, experiences, and house
              correspondence reserved for those on the list.
            </p>
            <p className="text-[#A8A9AD] italic">
              The house writes seldom, deliberately, and only to members.
            </p>
          </div>
        </motion.div>

        {/* Form — generous whitespace, hairline rules, no card chrome */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.0, delay: 0.15, ease: "easeOut" }}
          className="mt-20 sm:mt-24"
        >
          {success ? (
            <RegistrySuccess email={submittedEmail} />
          ) : (
            <form
              onSubmit={onSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-14 lg:gap-x-20 gap-y-14"
              noValidate
            >
              <Field label="First Name" required testId={null}>
                <RegistryInput
                  data-testid={TID.waitlistFirstName}
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  placeholder=""
                  autoComplete="given-name"
                />
              </Field>

              <Field label="Last Name" required>
                <RegistryInput
                  data-testid={TID.waitlistLastName}
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  placeholder=""
                  autoComplete="family-name"
                />
              </Field>

              <Field label="Email Address" required>
                <RegistryInput
                  data-testid={TID.waitlistEmail}
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder=""
                  autoComplete="email"
                />
              </Field>

              <Field label="Preferred Collection" required>
                <Select
                  value={form.preferred_collection}
                  onValueChange={(v) => update("preferred_collection", v)}
                >
                  <SelectTrigger
                    data-testid={TID.waitlistCollection}
                    className="bg-transparent border-0 border-b border-[rgba(245,242,235,0.18)] rounded-none px-0 py-3 text-[#F5F2EB] focus:ring-0 focus:border-[#C5A059] h-auto justify-between data-[placeholder]:text-[#A8A9AD]/40 text-[15px]"
                  >
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1311] border border-[rgba(197,160,89,0.25)] rounded-none text-[#F5F2EB]">
                    {COLLECTIONS.map((opt) => (
                      <SelectItem
                        key={opt}
                        value={opt}
                        data-testid={TID.waitlistCollectionOption(
                          opt.replace(/\s+/g, "-").toLowerCase(),
                        )}
                        className="rounded-none focus:bg-[#C5A059] focus:text-[#0C0F0A] cursor-pointer py-3 text-[15px]"
                      >
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Country">
                <RegistryInput
                  data-testid={TID.waitlistCountry}
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                  placeholder=""
                  autoComplete="country-name"
                />
              </Field>

              <Field label="Home Club">
                <RegistryInput
                  data-testid={TID.waitlistHomeClub}
                  value={form.home_club}
                  onChange={(e) => update("home_club", e.target.value)}
                  placeholder="Optional — Tell us where you play"
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Personal Notes">
                  <Textarea
                    data-testid={TID.waitlistNote}
                    rows={5}
                    value={form.personal_notes}
                    onChange={(e) =>
                      update("personal_notes", e.target.value)
                    }
                    placeholder="Preferred dial colors, commissioning requests, gifting inquiries, club affiliation, or anything else you'd like us to know."
                    className="bg-transparent border-0 border-b border-[rgba(245,242,235,0.18)] rounded-none px-0 py-3 text-[#F5F2EB] placeholder:text-[#A8A9AD]/35 placeholder:italic focus-visible:ring-0 focus-visible:border-[#C5A059] resize-none text-[15px] leading-relaxed"
                  />
                </Field>
              </div>

              {error && (
                <div
                  data-testid={TID.waitlistError}
                  className="md:col-span-2 text-[#e07a5f] text-sm tracking-wide"
                >
                  {error}
                </div>
              )}

              {/* CTA — premium, understated, centered */}
              <div className="md:col-span-2 mt-8 flex flex-col items-center text-center">
                <button
                  type="submit"
                  data-testid={TID.waitlistSubmit}
                  disabled={submitting}
                  className="group inline-flex items-center justify-center gap-5 border border-[#C5A059] text-[#C5A059] px-14 py-5 text-[11px] uppercase tracking-[0.42em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors duration-500 disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Request Access"}
                  <span
                    aria-hidden
                    className="inline-block w-8 h-px bg-current transition-all duration-500 group-hover:w-12"
                  />
                </button>
                <p className="mt-6 text-[11px] uppercase tracking-[0.32em] text-[#A8A9AD]/70">
                  Registry submissions are reviewed individually
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function RegistryInput(props) {
  return (
    <Input
      {...props}
      className="bg-transparent border-0 border-b border-[rgba(245,242,235,0.18)] rounded-none px-0 py-3 text-[#F5F2EB] placeholder:text-[#A8A9AD]/40 placeholder:italic focus-visible:ring-0 focus-visible:border-[#C5A059] h-auto text-[15px]"
    />
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="block mb-5 text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85">
        {label}
        {required ? <span className="text-[#C5A059] ml-1.5">·</span> : null}
      </Label>
      {children}
    </div>
  );
}

function RegistrySuccess({ email }) {
  const substackHref = email
    ? `${SUBSTACK_SUBSCRIBE_URL}?utm_source=registry&email=${encodeURIComponent(email)}`
    : SUBSTACK_SUBSCRIBE_URL;
  return (
    <div
      data-testid={TID.waitlistSuccess}
      className="py-16 text-center max-w-xl mx-auto"
    >
      <div className="flex items-center justify-center gap-4">
        <span className="block w-12 h-px bg-[#C5A059]" />
        <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
          Submission Received
        </span>
        <span className="block w-12 h-px bg-[#C5A059]" />
      </div>
      <h3 className="mt-10 font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] font-light leading-tight">
        Thank you.
      </h3>
      <p className="mt-8 text-[#C9C6BC] leading-[1.85] font-light text-[15px]">
        Your interest in CLEMENT has been registered. Members of the registry
        receive collection announcements, allocation opportunities and bespoke
        commissioning information in advance of any public release.
      </p>

      {/* Substack hand-off — one-click confirm */}
      <div className="hairline mt-12" />
      <p className="mt-10 text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
        One Step Remaining
      </p>
      <p className="mt-5 text-[#C9C6BC] leading-[1.85] font-light text-[15px]">
        Confirm your subscription to <span className="italic">The Course</span>,
        the house journal — the channel through which all allocation news and
        member updates are issued.
      </p>
      <a
        href={substackHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="waitlist-confirm-newsletter"
        className="group/cta mt-8 inline-flex items-center justify-center gap-5 border border-[#C5A059] text-[#C5A059] px-10 py-4 text-[11px] uppercase tracking-[0.42em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors duration-500"
      >
        Confirm Newsletter Subscription
        <span
          aria-hidden
          className="inline-block w-7 h-px bg-current transition-all duration-500 group-hover/cta:w-10"
        />
      </a>
      <p className="mt-8 text-[#A8A9AD]/80 italic text-sm">
        We will be in touch when there is something meaningful to share.
      </p>
    </div>
  );
}
